// ----------------------------------------------------------------------
// A baseline for a box that leads with a mark rather than with text.
//
// A key-value row and a phone card row put their label on the first line of
// the value by lining the two up on their first baseline. That is right for
// every value that starts with text, and wrong for one that starts with a
// glyph, a flag or a switch: a mark has no baseline, so the browser makes one
// up from its bottom edge, and the label is set level with the foot of the
// mark instead of beside it. Lined up that way, the Managers card's `Sign-in`
// sat 4px low of its marks, the vehicle page's `Booking` 6px low of its
// switch and a plate led by its flag 8px low of the plate.
//
// So a box that leads with a mark carries a line of text first: a zero-width
// space in a `::before`, centred across the box. Its baseline is where the
// baseline of a line of text centred on the mark would be, and the box takes
// it as its own, so a label lined up with it sits level with the mark's
// middle, wherever the box is put: on a line of text, first in a flex row, or
// as a grid item. The line is zero high, so the box does not grow. Where the
// box spaces its items with `gap`, the strut is one more item to space, so it
// takes that gap back with a negative margin and nothing moves.
// ----------------------------------------------------------------------

/**
 * The `sx` that centres a word on its capitals: `text-box` trims its line to
 * the capitals and the baseline, so a box that centres it centres the
 * letters, and not a line box that is tall at the top, as Heebo's is. A
 * letter in a square read a pixel high in every account mark and initial
 * (asked for on 2026-10-31: "Make sure the profile icons letters are center
 * and middle always"). Spread it on the element that holds the text itself:
 * a flex container hands it to no anonymous item. A browser without
 * `text-box` (Firefox, today) draws the word as before.
 */
export const capCentred = { textBox: 'trim-both cap alphabetic' } as const

export interface TextBaselineOptions {
  /**
   * The type the baseline is for, where it is not the box's own: a mark
   * column lined up with a caption label states the caption's size.
   */
  fontSize?: string | number
  /** The box's own `gap`, in spacing steps, which the strut gives back. */
  gap?: number
}

/**
 * The `sx` that gives a flex box leading with a mark a text baseline centred
 * on it. Spread it into the box's own `sx`.
 */
export const textBaseline = ({ fontSize, gap = 0 }: TextBaselineOptions = {}) =>
  ({
    '&::before': {
      content: '"\\200B"',
      alignSelf: 'center',
      lineHeight: 0,
      ...(fontSize === undefined ? null : { fontSize }),
      ...(gap ? { mr: -gap } : null),
    },
  }) as const
